import * as THREE from "three";
import { ITEMS } from "../shared/items";
import type { ChestInfo, SignInfo } from "../shared/protocol";
import { pixelTexture, tileCanvas, T, itemCanvas, heartCanvas } from "./textures";
import { paintSkin, mcBoxUV } from "./skin";
import { FONT } from "./font";

const PX = 1.8 / 32; // one skin pixel in world units

// ---------- extruded item sprites ----------
// Minecraft draws held items as their 16x16 sprite pushed out one pixel deep.

const itemGeoCache = new Map<string, THREE.BufferGeometry>();

export function itemGeometry(id: string): THREE.BufferGeometry {
  const cached = itemGeoCache.get(id);
  if (cached) return cached;
  const c = itemCanvas(id);
  const d = c.getContext("2d")!.getImageData(0, 0, 16, 16).data;
  const opaque = (x: number, y: number) => x >= 0 && y >= 0 && x < 16 && y < 16 && d[(y * 16 + x) * 4 + 3]! > 128;
  const pos: number[] = [], col: number[] = [], idx: number[] = [];
  const s = 1 / 16, t = 1 / 32;
  const color = new THREE.Color();
  const quad = (a: number[], b: number[], cc: number[], dd: number[], f: number) => {
    const base = pos.length / 3;
    pos.push(...a, ...b, ...cc, ...dd);
    for (let k = 0; k < 4; k++) col.push(color.r * f, color.g * f, color.b * f);
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
    if (!opaque(x, y)) continue;
    const i = (y * 16 + x) * 4;
    color.setRGB(d[i]! / 255, d[i + 1]! / 255, d[i + 2]! / 255, THREE.SRGBColorSpace);
    const x0 = x * s - 0.5, x1 = x0 + s, y1 = 0.5 - y * s, y0 = y1 - s;
    quad([x0, y0, t], [x1, y0, t], [x1, y1, t], [x0, y1, t], 1);
    quad([x1, y0, -t], [x0, y0, -t], [x0, y1, -t], [x1, y1, -t], 0.8);
    if (!opaque(x - 1, y)) quad([x0, y0, -t], [x0, y0, t], [x0, y1, t], [x0, y1, -t], 0.6);
    if (!opaque(x + 1, y)) quad([x1, y0, t], [x1, y0, -t], [x1, y1, -t], [x1, y1, t], 0.6);
    if (!opaque(x, y - 1)) quad([x0, y1, t], [x1, y1, t], [x1, y1, -t], [x0, y1, -t], 0.9);
    if (!opaque(x, y + 1)) quad([x0, y0, -t], [x1, y0, -t], [x1, y0, t], [x0, y0, t], 0.5);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  itemGeoCache.set(id, g);
  return g;
}

export const itemMaterial = new THREE.MeshBasicMaterial({ vertexColors: true });

// ---------- players ----------

function skinBox(tex: THREE.Material, w: number, h: number, d: number, u: number, v: number, pivotTop = false, inflate = 0) {
  const geo = new THREE.BoxGeometry((w + inflate) * PX, (h + inflate) * PX, (d + inflate) * PX);
  mcBoxUV(geo, u, v, w, h, d);
  if (pivotTop) geo.translate(0, (-h * PX) / 2, 0);
  return new THREE.Mesh(geo, tex);
}

let armorTex: THREE.Texture | null = null;
function armorTexture() {
  if (armorTex) return armorTex;
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  const g = c.getContext("2d")!;
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
    const edge = x === 0 || y === 0 || x === 15 || y === 15;
    const v = edge ? 120 : 200 + Math.floor(Math.random() * 55);
    g.fillStyle = `rgb(${v},${v},${v})`;
    g.fillRect(x, y, 1, 1);
  }
  armorTex = pixelTexture(c);
  return armorTex;
}

export class PlayerModel {
  root = new THREE.Group();
  private body = new THREE.Group();
  private head: THREE.Mesh;
  private armL: THREE.Mesh;
  private armR: THREE.Mesh;
  private legL: THREE.Mesh;
  private legR: THREE.Mesh;
  private armorParts: THREE.Mesh[][] = [[], [], [], []];
  private mats: THREE.MeshLambertMaterial[] = [];
  private skinTex: THREE.Texture;
  private tag: THREE.Sprite;
  private hearts: { sprite: THREE.Sprite; canvas: HTMLCanvasElement; tex: THREE.CanvasTexture; hp: number };
  private held: THREE.Mesh;
  private heldId = "";
  private walk = 0;
  private hurtUntil = 0;
  private swingT = 1;
  // interpolation buffer
  snaps: { t: number; x: number; y: number; z: number; yaw: number; pitch: number }[] = [];
  x = 0; y = 0; z = 0; yaw = 0; pitch = 0;
  sneak = false;
  /** Pulling a bow back: both arms come up to aim. */
  drawing = false;
  /** Your own model (third person) hides its nametag and hearts, like Minecraft. */
  showTag = true;

  constructor(public id: number, public name: string) {
    this.skinTex = pixelTexture(paintSkin(name));
    const skin = new THREE.MeshLambertMaterial({ map: this.skinTex, alphaTest: 0.5 });
    this.mats.push(skin);

    this.head = skinBox(skin, 8, 8, 8, 0, 0);
    this.head.geometry.translate(0, 4 * PX, 0); // pivot at the neck
    this.head.position.y = 24 * PX;
    const torso = skinBox(skin, 8, 12, 4, 16, 16);
    torso.position.y = 18 * PX;
    this.armR = skinBox(skin, 4, 12, 4, 40, 16, true);
    this.armL = skinBox(skin, 4, 12, 4, 40, 16, true);
    this.armR.position.set(6 * PX, 24 * PX, 0);
    this.armL.position.set(-6 * PX, 24 * PX, 0);
    this.legR = skinBox(skin, 4, 12, 4, 0, 16, true);
    this.legL = skinBox(skin, 4, 12, 4, 0, 16, true);
    this.legR.position.set(2 * PX, 12 * PX, 0);
    this.legL.position.set(-2 * PX, 12 * PX, 0);
    this.body.add(this.head, torso, this.armL, this.armR, this.legL, this.legR);

    // Armor shells, tinted per material when equipped.
    const shell = (parent: THREE.Object3D, w: number, hh: number, d: number, slot: number, y = 0, pivotTop = false) => {
      const m = new THREE.MeshLambertMaterial({ map: armorTexture(), color: "#fff" });
      this.mats.push(m);
      const geo = new THREE.BoxGeometry(w * PX, hh * PX, d * PX);
      if (pivotTop) geo.translate(0, (-hh * PX) / 2, 0);
      const b = new THREE.Mesh(geo, m);
      b.position.y = y;
      b.visible = false;
      parent.add(b);
      this.armorParts[slot]!.push(b);
    };
    shell(this.head, 9, 9, 9, 0, 4 * PX);
    shell(torso, 9, 13, 5, 1);
    shell(this.armL, 5, 5, 5, 1, 0, true);
    shell(this.armR, 5, 5, 5, 1, 0, true);
    shell(torso, 8.6, 3, 4.6, 2, -5 * PX);
    shell(this.legL, 4.6, 9, 4.6, 2, 0, true);
    shell(this.legR, 4.6, 9, 4.6, 2, 0, true);
    shell(this.legL, 4.8, 4, 4.8, 3, -8.2 * PX, true);
    shell(this.legR, 4.8, 4, 4.8, 3, -8.2 * PX, true);

    // Held item: sprite in the arm's YZ plane, tip forward, handle in the fist.
    this.held = new THREE.Mesh(new THREE.BufferGeometry(), itemMaterial);
    const size = 0.62;
    this.held.scale.setScalar(size);
    this.held.rotation.set(-0.35, Math.PI / 2, 0);
    this.held.position.set(0, -10 * PX + 0.3 * size, -1 * PX - 0.3 * size);
    this.held.visible = false;
    this.armR.add(this.held);

    this.tag = nameTag(name);
    this.tag.position.y = 2.22;
    this.hearts = heartsTag();
    this.hearts.sprite.position.y = 2.03;
    this.setHealth(20);
    this.root.add(this.body, this.tag, this.hearts.sprite);
  }

  setArmor(armor: (string | null)[]) {
    armor.forEach((id, slot) => {
      for (const part of this.armorParts[slot]!) {
        part.visible = !!id;
        if (id) (part.material as THREE.MeshLambertMaterial).color.set(ITEMS[id]!.color);
      }
    });
  }

  setHeld(id: string) {
    if (id === this.heldId) return;
    this.heldId = id;
    this.held.visible = !!id;
    if (id) this.held.geometry = itemGeometry(id);
  }

  /** World position of a point on the held item's sprite (e.g. the tip of a fishing rod). */
  heldPoint(x: number, y: number): THREE.Vector3 {
    this.root.updateMatrixWorld(true);
    return this.held.localToWorld(new THREE.Vector3(x, y, 0));
  }

  /** Ten hearts under the nametag, like the LBSG servers showed. */
  setHealth(hp: number) {
    const h = this.hearts;
    if (h.hp === hp) return;
    h.hp = hp;
    const g = h.canvas.getContext("2d")!;
    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, h.canvas.width, h.canvas.height);
    for (let i = 0; i < 10; i++) {
      const left = hp - i * 2;
      g.drawImage(heartCanvas(left >= 2 ? "full" : left >= 1 ? "half" : "empty"), i * HEART_STEP, 0, 27, 27);
    }
    h.tex.needsUpdate = true;
  }

  hurt() {
    this.hurtUntil = performance.now() + 350;
  }
  swing() {
    if (this.swingT >= 1) this.swingT = 0;
  }

  /** Interpolate ~100ms behind the newest snapshot. */
  update(dt: number) {
    const renderT = performance.now() - 110;
    const s = this.snaps;
    while (s.length > 2 && s[1]!.t <= renderT) s.shift();
    const px = this.x, pz = this.z;
    if (s.length >= 2 && s[0]!.t <= renderT) {
      const a = s[0]!, b = s[1]!;
      const k = Math.min(1, (renderT - a.t) / Math.max(1, b.t - a.t));
      this.x = a.x + (b.x - a.x) * k;
      this.y = a.y + (b.y - a.y) * k;
      this.z = a.z + (b.z - a.z) * k;
      let dy = b.yaw - a.yaw;
      dy = Math.atan2(Math.sin(dy), Math.cos(dy));
      this.yaw = a.yaw + dy * k;
      this.pitch = a.pitch + (b.pitch - a.pitch) * k;
    } else if (s.length) {
      const a = s[s.length - 1]!;
      Object.assign(this, { x: a.x, y: a.y, z: a.z, yaw: a.yaw, pitch: a.pitch });
    }
    const speed = Math.hypot(this.x - px, this.z - pz) / Math.max(dt, 1e-3);
    this.animate(dt, speed);
  }

  animate(dt: number, speed: number) {
    this.root.position.set(this.x, this.y - (this.sneak ? 0.12 : 0), this.z);
    this.body.rotation.y = this.yaw;
    this.head.rotation.x = this.pitch;
    this.body.rotation.x = 0;
    // Minecraft's limb swing: legs and arms in opposition, amplitude by speed.
    this.walk += dt * Math.min(speed, 6) * 2.6;
    const amp = Math.min(1, speed / 4.3) * 1.1;
    const sw = Math.sin(this.walk) * amp;
    this.legL.rotation.x = sw;
    this.legR.rotation.x = -sw;
    this.armL.rotation.x = -sw * 0.9;
    this.armR.rotation.x = sw * 0.9;
    this.armL.rotation.z = -0.05;
    this.armR.rotation.z = 0.05;
    if (this.swingT < 1) {
      this.swingT = Math.min(1, this.swingT + dt * 4.5);
      this.armR.rotation.x = Math.sin(this.swingT * Math.PI) * 1.6 + 0.3;
    } else if (this.heldId) this.armR.rotation.x = sw * 0.5 + 0.3;
    if (this.drawing) {
      // Both arms up along the line of sight, bow held out in front.
      this.armR.rotation.set(Math.PI / 2 + this.pitch, 0, -0.1);
      this.armL.rotation.set(Math.PI / 2 + this.pitch, 0, 0.45);
    }
    this.tag.visible = this.hearts.sprite.visible = this.showTag && !this.sneak;
    const red = performance.now() < this.hurtUntil;
    for (const m of this.mats) m.emissive.setRGB(red ? 0.6 : 0, 0, 0);
  }

  dispose() {
    this.skinTex.dispose();
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh && o !== this.held) o.geometry.dispose();
    });
    this.hearts.tex.dispose();
    this.hearts.sprite.material.dispose();
    (this.tag.material as THREE.SpriteMaterial).map?.dispose();
    this.tag.material.dispose();
  }
}

const HEART_STEP = 24; // hearts overlap a pixel, like the HUD row (9px icons drawn 3x)

function heartsTag() {
  const canvas = document.createElement("canvas");
  canvas.width = HEART_STEP * 9 + 27;
  canvas.height = 27;
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  const h = 0.11;
  sprite.scale.set((h * canvas.width) / canvas.height, h, 1);
  sprite.renderOrder = 10;
  return { sprite, canvas, tex, hp: -1 };
}

function nameTag(name: string): THREE.Sprite {
  const c = document.createElement("canvas");
  const g = c.getContext("2d")!;
  g.font = `32px ${FONT}`;
  const w = Math.ceil(g.measureText(name).width) + 12;
  c.width = w;
  c.height = 40;
  g.fillStyle = "rgba(0,0,0,0.25)";
  g.fillRect(0, 0, w, 40);
  g.font = `32px ${FONT}`;
  g.textBaseline = "middle";
  g.fillStyle = "#fff";
  g.fillText(name, 6, 22);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
  sp.scale.set((w / 40) * 0.27, 0.27, 1);
  sp.renderOrder = 10;
  return sp;
}

// ---------- particles ----------

const MAX_PARTICLES = 256;

/** Crit sparks: the little tan stars that burst off someone you hit on the way down. */
export class Particles {
  points: THREE.Points;
  private pos = new Float32Array(MAX_PARTICLES * 3);
  private col = new Float32Array(MAX_PARTICLES * 3);
  private vel = new Float32Array(MAX_PARTICLES * 3);
  private life = new Float32Array(MAX_PARTICLES);
  private next = 0;

  constructor() {
    const c = document.createElement("canvas");
    c.width = c.height = 8;
    const g = c.getContext("2d")!;
    g.fillStyle = "#fff";
    // A chunky plus-shaped star.
    g.fillRect(3, 1, 2, 6);
    g.fillRect(1, 3, 6, 2);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3));
    geo.setDrawRange(0, 0);
    this.points = new THREE.Points(
      geo,
      new THREE.PointsMaterial({ map: pixelTexture(c), size: 0.16, vertexColors: true, alphaTest: 0.5, sizeAttenuation: true }),
    );
    this.points.frustumCulled = false;
    this.life.fill(0);
  }

  crit(x: number, y: number, z: number) {
    for (let n = 0; n < 18; n++) {
      const i = this.next;
      this.next = (this.next + 1) % MAX_PARTICLES;
      const a = Math.random() * Math.PI * 2, up = Math.random() * 2 - 0.6;
      const sp = 2 + Math.random() * 2.5;
      this.pos.set([x + (Math.random() - 0.5) * 0.6, y + (Math.random() - 0.5) * 1.2, z + (Math.random() - 0.5) * 0.6], i * 3);
      this.vel.set([Math.cos(a) * sp, up * 2.5, Math.sin(a) * sp], i * 3);
      const shade = 0.6 + Math.random() * 0.4;
      this.col.set([shade, shade * 0.85, shade * 0.6], i * 3);
      this.life[i] = 0.5 + Math.random() * 0.35;
    }
  }

  /** One spark left hanging in the air: the trail behind a fully drawn arrow. */
  spark(x: number, y: number, z: number) {
    const i = this.next;
    this.next = (this.next + 1) % MAX_PARTICLES;
    this.pos.set([x, y, z], i * 3);
    this.vel.set([(Math.random() - 0.5) * 0.6, Math.random() * 0.4, (Math.random() - 0.5) * 0.6], i * 3);
    const shade = 0.7 + Math.random() * 0.3;
    this.col.set([shade, shade * 0.85, shade * 0.6], i * 3);
    this.life[i] = 0.35 + Math.random() * 0.2;
  }

  update(dt: number) {
    let alive = 0;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      if (this.life[i]! <= 0) {
        this.pos[i * 3 + 1] = -1000; // parked out of sight
        continue;
      }
      alive++;
      this.life[i]! -= dt;
      const drag = Math.max(0, 1 - dt * 4);
      for (let k = 0; k < 3; k++) {
        this.vel[i * 3 + k]! *= drag;
        this.pos[i * 3 + k]! += this.vel[i * 3 + k]! * dt;
      }
      this.vel[i * 3 + 1]! -= 4 * dt;
    }
    const geo = this.points.geometry;
    geo.setDrawRange(0, alive ? MAX_PARTICLES : 0);
    geo.attributes.position!.needsUpdate = true;
    geo.attributes.color!.needsUpdate = true;
  }
}

// ---------- chests ----------

let chestMats: THREE.Material[] | null = null;
function chestMaterials() {
  if (chestMats) return chestMats;
  const side = new THREE.MeshLambertMaterial({ map: pixelTexture(tileCanvas(T.CHEST_SIDE)) });
  const front = new THREE.MeshLambertMaterial({ map: pixelTexture(tileCanvas(T.CHEST_FRONT)) });
  const top = new THREE.MeshLambertMaterial({ map: pixelTexture(tileCanvas(T.CHEST_TOP)) });
  chestMats = [side, side, top, top, side, front];
  return chestMats;
}
const latchMat = new THREE.MeshLambertMaterial({ color: "#c9c9c9" });

export class ChestModel {
  root = new THREE.Group();
  private lid: THREE.Group;
  private lidAngle = 0;
  open = false;
  constructor(public info: ChestInfo, faceYaw: number) {
    const mats = chestMaterials();
    // Classic chest: 14x10x14 base, 14x5x14 lid hinged at the back, latch on the front.
    const base = new THREE.Mesh(new THREE.BoxGeometry(14 / 16, 10 / 16, 14 / 16), mats);
    base.position.y = 5 / 16;
    this.lid = new THREE.Group();
    const lidMesh = new THREE.Mesh(new THREE.BoxGeometry(14 / 16, 5 / 16, 14 / 16), mats);
    lidMesh.position.set(0, 2.5 / 16, -7 / 16);
    const latch = new THREE.Mesh(new THREE.BoxGeometry(2 / 16, 4 / 16, 1 / 16), latchMat);
    latch.position.set(0, 0, -14.5 / 16);
    this.lid.position.set(0, 10 / 16, 7 / 16);
    this.lid.add(lidMesh, latch);
    const pivot = new THREE.Group();
    pivot.add(base, this.lid);
    pivot.rotation.y = faceYaw;
    this.root.add(pivot);
    this.root.position.set(info.x + 0.5, info.y, info.z + 0.5);
    this.open = info.open;
  }
  update(dt: number) {
    const target = this.open ? 1.2 : 0;
    this.lidAngle += (target - this.lidAngle) * Math.min(1, dt * 8);
    this.lid.rotation.x = this.lidAngle;
  }
}

// ---------- signs ----------

let signWood: THREE.MeshLambertMaterial | null = null;
let postMat: THREE.MeshLambertMaterial | null = null;

export function signTextCanvas(lines: string[]): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 384;
  c.height = 192;
  const g = c.getContext("2d")!;
  g.imageSmoothingEnabled = false;
  const planks = tileCanvas(T.PLANKS);
  for (let y = 0; y < 192; y += 64) for (let x = 0; x < 384; x += 64) g.drawImage(planks, x, y, 64, 64);
  g.fillStyle = "#0c0c0c";
  g.font = `36px ${FONT}`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  lines.forEach((l, i) => g.fillText(l, 192, 28 + i * 45, 372));
  return c;
}

export class SignModel {
  root = new THREE.Group();
  board: THREE.Mesh;
  private textTex: THREE.Texture;
  constructor(public info: SignInfo) {
    signWood ??= new THREE.MeshLambertMaterial({ map: pixelTexture(tileCanvas(T.PLANKS)) });
    postMat ??= new THREE.MeshLambertMaterial({ map: pixelTexture(tileCanvas(T.LOG_SIDE)) });
    const textTex = (this.textTex = pixelTexture(signTextCanvas(info.lines)));
    // Mipmaps keep the lettering legible from across the arena.
    textTex.minFilter = THREE.LinearMipmapLinearFilter;
    textTex.generateMipmaps = true;
    const text = new THREE.MeshLambertMaterial({ map: textTex });
    const th = 1.5 / 16;
    this.board = new THREE.Mesh(new THREE.BoxGeometry(1, 0.5, th), [signWood, signWood, signWood, signWood, text, signWood]);
    const pivot = new THREE.Group();
    if (info.wall === -1) {
      this.board.position.y = 0.58 + 0.25;
      const post = new THREE.Mesh(new THREE.BoxGeometry(th, 0.58, th), postMat);
      post.position.y = 0.29;
      pivot.add(post);
      pivot.rotation.y = (info.rot / 16) * Math.PI * 2;
    } else {
      this.board.position.set(0, 0.5, -0.5 + th / 2);
      pivot.rotation.y = [0, Math.PI, Math.PI / 2, -Math.PI / 2][info.wall]!;
    }
    pivot.add(this.board);
    this.root.add(pivot);
    this.root.position.set(info.x + 0.5, info.y, info.z + 0.5);
  }
  dispose() {
    this.textTex.dispose();
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
  }
}
